/**
 * PremierFrame — felles ramme for fanene i Premier (#156, steg 6).
 *
 * Belønninger, Butikk og Merker har samme topp i skissene: ScreenHeader
 * «Premier» med avataren, og underfanene rett under. Belønninger legger
 * lommebokkortet mellom toppen og fanene (`wallet`); Butikk og Merker har
 * saldopillen i toppen (`trailing` med `BalancePill`).
 *
 * Skjermene som bruker rammen står i `drawsOwnHeader` (skallet tegner ikke sin
 * egen topp) og er merket `ownTabs` i SubTabsLayout (layouten legger ikke
 * fanene over skjermen). Rammen står i alle tilstander — lasting, feil, tomt —
 * så navigasjonen aldri forsvinner.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ScreenHeader } from '../../components/ScreenHeader';
import { formatPoints } from '../../i18n/formatters';
import { SubTabs } from '../dashboard/SubTabsLayout';

/** Flaten under rammen; også for en Premier-skjerm som står uten den. */
export const PREMIER_MAIN = 'flex min-h-dvh flex-col gap-3 px-5 pt-4 pb-6 md:px-8';

/** Lommebokkortet: på Belønninger (lenke til `/poeng`) og saldokortet på Lommebok. */
export const WALLET_SURFACE = 'rounded-xl border border-card-line bg-wallet text-ink shadow-md backdrop-blur-card';

export function PremierFrame({
  testId,
  trailing,
  wallet,
  children,
}: {
  testId: string;
  /** I toppen foran avataren, typisk `BalancePill`. */
  trailing?: ReactNode;
  /** Mellom toppen og underfanene (lommebokkortet på Belønninger). */
  wallet?: ReactNode;
  children: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <main data-testid={testId} className={PREMIER_MAIN}>
      <ScreenHeader title={t('nav.rewards')} trailing={trailing} />
      {wallet}
      <SubTabs group="rewards" />
      {children}
    </main>
  );
}

const BALANCE_PILL =
  'tnum inline-flex h-11 shrink-0 items-center rounded-pill bg-lime-bg px-3.5 text-body font-extrabold text-lime';

/**
 * Saldoen som pille i toppen, som lenke til lommeboken. Ukjent saldo (henter
 * eller feilet) er ikke 0, så pillen viser da en strek.
 */
export function BalancePill({
  balance,
  'data-testid': testId,
}: {
  balance: number | undefined;
  /** Bokstavelig på kallstedet, så testid-kontrakten ser den. */
  'data-testid': string;
}): ReactNode {
  const { t } = useTranslation();
  if (typeof balance !== 'number') {
    return (
      <Link to="/poeng" data-testid={testId} aria-label={t('rewards.balancePill.pending')} className={BALANCE_PILL}>
        {t('rewards.wallet.pending')}
      </Link>
    );
  }
  const points = formatPoints(balance);
  return (
    <Link to="/poeng" data-testid={testId} aria-label={t('rewards.balancePill.label', { points })} className={BALANCE_PILL}>
      {t('rewards.points', { points })}
    </Link>
  );
}
